import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, ArrowRight, BarChart3, Clock, Calendar, MessageSquare, CreditCard, ChevronRight } from "lucide-react";
import { Spinner } from "@shared/components/ui";
import { useAuth } from "@shared/lib/auth";
import { useToast } from "@shared/lib/toast";
import { apiFetch, ApiError } from "@shared/lib/api";
import { matchActions } from "../lib/actionMatcher";
import { useSearchIntent } from "../lib/useSearchIntent";
import { SearchSuggestion } from "./SearchSuggestion";
import { CapabilityHint } from "./CapabilityHint";
import type { SearchResult } from "../lib/useGlobalSearch";
import type { UserRole } from "@shared/lib/types";
import quickActionsData from "../../../backend/childcare_api/config/quick_actions.json";


const TYPE_STYLES: Record<string, { label: string; bg: string }> = {
  child: { label: "Child", bg: "bg-[#2D5A4A]" },
  parent: { label: "Parent", bg: "bg-[#7A8C6B]" },
  employee: { label: "Employee", bg: "bg-[#4A6FA5]" },
  director: { label: "Director", bg: "bg-[#3D7A8A]" },
  lead: { label: "Lead", bg: "bg-[#B8956B]" },
  classroom: { label: "Classroom", bg: "bg-[#2D5A4A]" },
  invoice: { label: "Invoice", bg: "bg-[#C4785C]" },
  incident_report: { label: "Incident", bg: "bg-[#6B5B8C]" },
};

type ActionCategory = "create" | "navigate" | "reports";

const CATEGORY_META: Record<
  ActionCategory,
  { label: string; icon: typeof Plus; order: number }
> = {
  create: { label: "Create", icon: Plus, order: 0 },
  navigate: { label: "Navigate", icon: ArrowRight, order: 1 },
  reports: { label: "Reports", icon: BarChart3, order: 2 },
};

interface QuickAction {
  type: "action";
  category: ActionCategory;
  roles: UserRole[];
  id: string;
  label: string;
  subtitle: string;
  keywords: string[];
  path: string;
  action?: "messaging:compose";
}

const QUICK_ACTIONS: QuickAction[] = (quickActionsData as { actions: Omit<QuickAction, "type">[] }).actions.map(
  (a) => ({ ...a, type: "action" as const })
);

function getResultPath(result: SearchResult): string {
  switch (result.record_type) {
    case "child":
      return `/?panel=families&tab=children&id=${result.id}`;
    case "parent":
      return `/?panel=families&tab=parents&id=${result.id}`;
    case "employee":
    case "director":
      return `/employees/${result.id}`;
    case "lead":
      return `/my-center/leads/${result.id}`;
    case "classroom":
      return `/students/classrooms/${result.id}`;
    case "invoice":
      return `/?panel=billing&tab=invoices&id=${result.id}`;
    case "incident_report":
      return `/students/incidents/${result.id}`;
    default:
      return "/";
  }
}

interface SubAction {
  id: string;
  label: string;
  icon: typeof Clock;
}

const STAFF_ACTIONS: SubAction[] = [
  { id: "clock", label: "Clock In", icon: Clock },
  { id: "schedule", label: "Schedule", icon: Calendar },
];

const RESULT_ACTIONS: Record<string, SubAction[]> = {
  employee: STAFF_ACTIONS,
  director: STAFF_ACTIONS,
  parent: [
    { id: "billing", label: "Billing", icon: CreditCard },
    { id: "message", label: "Message", icon: MessageSquare },
  ],
  child: [
    { id: "checkin", label: "Check In", icon: Clock },
  ],
};

// Filter sub-actions based on user role and target result
function getSubActions(recordType: string, resultId: number, userRole: UserRole | undefined, userId: number | undefined): SubAction[] | undefined {
  const actions = RESULT_ACTIONS[recordType];
  if (!actions) return undefined;

  // Teachers can only clock themselves in/out, not other employees
  if ((recordType === "employee" || recordType === "director") && userRole === "teacher") {
    if (resultId !== userId) {
      return actions.filter((a) => a.id !== "clock");
    }
  }
  return actions;
}

interface GlobalSearchProps {
  isOpen: boolean;
  query: string;
  setQuery: (q: string) => void;
  results: SearchResult[];
  gatedCount: number;
  loading: boolean;
  close: () => void;
}

export function GlobalSearch({
  isOpen,
  query,
  setQuery,
  results,
  gatedCount,
  loading,
  close,
}: GlobalSearchProps) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [subActionIndex, setSubActionIndex] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [clockStatus, setClockStatus] = useState<Record<number, { status: "loading" | "clocked_in" | "clocked_out"; timeEntryId?: number }>>({});
  const [attendanceStatus, setAttendanceStatus] = useState<Record<number, { status: "loading" | "checked_in" | "checked_out"; sessionId?: number }>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const userRole = user?.role as UserRole | undefined;

  // LLM intent suggestion — async, debounced 500ms, rendered above
  // QUICK_ACTIONS. When present it occupies index 0 in the keyboard list.
  const { suggestion } = useSearchIntent(query);
  const suggestionCount = suggestion ? 1 : 0;

  // Filter quick actions by role and query. The matcher expands the query via
  // the shared synonym/phrase map (same vocabulary the backend uses for record
  // search) so "boo boo" or "incidnet" surface the Log Incident action even
  // though those exact words don't appear in any action's keywords.
  const matchedActions = useMemo(
    () => matchActions(QUICK_ACTIONS, query, userRole),
    [query, userRole]
  );

  // Group matched actions by category
  const groupedActions = useMemo(() => {
    const groups = new Map<ActionCategory, QuickAction[]>();
    for (const action of matchedActions) {
      const list = groups.get(action.category);
      if (list) list.push(action);
      else groups.set(action.category, [action]);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => CATEGORY_META[a].order - CATEGORY_META[b].order)
      .map(([category, actions]) => ({ category, actions }));
  }, [matchedActions]);

  // Total navigable items: suggestion (0..1), then matched actions, then API results.
  const totalItems = suggestionCount + matchedActions.length + results.length;
  // Offset where matchedActions begin in the global index.
  const matchedActionsOffset = suggestionCount;
  // Offset where API record results begin.
  const resultsOffset = suggestionCount + matchedActions.length;

  // Focus input when modal opens, clear stale status caches
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
      setHighlightedIndex(-1);
      setClockStatus({});
      setAttendanceStatus({});
    }
  }, [isOpen]);

  // Clamp highlight when total items shrinks (don't reset — preserves keyboard position)
  useEffect(() => {
    setHighlightedIndex((prev) => {
      if (prev >= totalItems) return totalItems > 0 ? totalItems - 1 : -1;
      return prev;
    });
  }, [totalItems]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-search-item]");
    items[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  // Reset expansion when query changes
  useEffect(() => {
    setExpandedIndex(null);
    setSubActionIndex(0);
  }, [query]);

  function handleSelectAction(action: QuickAction) {
    close();
    if (action.action) {
      window.dispatchEvent(new CustomEvent(action.action));
    } else {
      navigate(action.path);
    }
  }

  function handleSelectSuggestion() {
    if (!suggestion) return;
    close();

    if (suggestion.kind === "question") {
      // Question suggestions navigate to the answer's contextual page (e.g.
      // attendance for absent-today). If the registry returns null
      // navigate_to, just close — the user has the summary in hand.
      if (suggestion.navigate_to) navigate(suggestion.navigate_to);
      return;
    }

    const target = suggestion.target_record;
    const kind = suggestion.prefill_kind;

    if (suggestion.action) {
      // For messaging:compose, mirror handleSubAction's event detail shape so
      // the compose drawer can pre-select the resolved parent/employee.
      if (suggestion.action === "messaging:compose" && target && kind === "recipient_id") {
        const recipientType =
          target.record_type === "parent" ? "parent" : "employee";
        window.dispatchEvent(
          new CustomEvent("messaging:compose", {
            detail: {
              recipientId: target.id,
              recipientType,
              recipientName: target.display_name,
            },
          })
        );
      } else {
        window.dispatchEvent(new CustomEvent(suggestion.action));
      }
      return;
    }

    let path = suggestion.path;
    if (kind && target) {
      const sep = path.includes("?") ? "&" : "?";
      path = `${path}${sep}${kind}=${target.id}`;
    }
    navigate(path);
  }

  function navigateToResult(result: SearchResult) {
    close();
    navigate(getResultPath(result));
  }

  // Fetch clock/attendance status when submenu expands
  useEffect(() => {
    if (expandedIndex === null) return;
    const resultIndex = expandedIndex - resultsOffset;
    const result = results[resultIndex];
    if (!result) return;

    if ((result.record_type === "employee" || result.record_type === "director") && !clockStatus[result.id]) {
      setClockStatus((prev) => ({ ...prev, [result.id]: { status: "loading" } }));
      apiFetch<{ clocked_in: boolean; time_entry?: { id: number } }>(`/api/v1/time_entries/current?user_id=${result.id}`)
        .then((res) => {
          setClockStatus((prev) => ({
            ...prev,
            [result.id]: {
              status: res.data.clocked_in ? "clocked_in" : "clocked_out",
              timeEntryId: res.data.time_entry?.id,
            },
          }));
        })
        .catch(() => {
          setClockStatus((prev) => ({ ...prev, [result.id]: { status: "clocked_out" } }));
        });
    }

    if (result.record_type === "child" && !attendanceStatus[result.id]) {
      setAttendanceStatus((prev) => ({ ...prev, [result.id]: { status: "loading" } }));
      const today = new Date().toISOString().split("T")[0];
      apiFetch<{ attendance_sessions: Array<{ id: number; attendance_date: string; check_in_at: string | null; check_out_at: string | null }> }>(
        `/api/v1/attendance_sessions?child_id=${result.id}`
      )
        .then((res) => {
          // Filter to today's sessions client-side (backend returns all recent sessions)
          const todaySessions = res.data.attendance_sessions?.filter((s) => s.attendance_date === today);
          const session = todaySessions?.[0];
          if (session && session.check_in_at && !session.check_out_at) {
            setAttendanceStatus((prev) => ({ ...prev, [result.id]: { status: "checked_in", sessionId: session.id } }));
          } else {
            setAttendanceStatus((prev) => ({ ...prev, [result.id]: { status: "checked_out", sessionId: session?.id } }));
          }
        })
        .catch(() => {
          setAttendanceStatus((prev) => ({ ...prev, [result.id]: { status: "checked_out" } }));
        });
    }
  }, [expandedIndex, resultsOffset, results]);

  async function handleSubAction(result: SearchResult, action: SubAction) {
    if (action.id === "schedule") {
      close();
      navigate(`/?panel=employees&tab=scheduling&id=${result.id}`);
      return;
    }
    if (action.id === "billing") {
      close();
      navigate(`/?panel=billing&tab=invoices&parent_id=${result.id}`);
      return;
    }
    if (action.id === "message") {
      close();
      const recipientType = result.record_type === "parent" ? "parent" : "employee";
      window.dispatchEvent(
        new CustomEvent("messaging:compose", {
          detail: { recipientId: result.id, recipientType, recipientName: result.display_name },
        })
      );
      return;
    }
    if (action.id === "clock") {
      const status = clockStatus[result.id];
      try {
        if (status?.status === "clocked_in" && status.timeEntryId) {
          await apiFetch(`/api/v1/time_entries/${status.timeEntryId}/clock_out`, { method: "POST" });
          toast.success(`${result.display_name} clocked out`);
          setClockStatus((prev) => ({ ...prev, [result.id]: { status: "clocked_out" } }));
        } else {
          const res = await apiFetch<{ time_entry: { id: number } }>("/api/v1/time_entries/clock_in", { method: "POST", body: { user_id: result.id } });
          toast.success(`${result.display_name} clocked in`);
          setClockStatus((prev) => ({ ...prev, [result.id]: { status: "clocked_in", timeEntryId: res.data.time_entry?.id } }));
        }
        close();
      } catch (err) {
        toast.error("Clock action failed", err instanceof ApiError ? err.errors.join(", ") : "An unexpected error occurred");
      }
      return;
    }
    if (action.id === "checkin") {
      const status = attendanceStatus[result.id];
      try {
        if (status?.status === "checked_in" && status.sessionId) {
          await apiFetch(`/api/v1/attendance_sessions/${status.sessionId}/check_out`, { method: "POST" });
          toast.success(`${result.display_name} checked out`);
          setAttendanceStatus((prev) => ({ ...prev, [result.id]: { status: "checked_out" } }));
        } else if (status?.sessionId) {
          await apiFetch(`/api/v1/attendance_sessions/${status.sessionId}/check_in`, { method: "POST" });
          toast.success(`${result.display_name} checked in`);
          setAttendanceStatus((prev) => ({ ...prev, [result.id]: { status: "checked_in", sessionId: status.sessionId } }));
        } else {
          close();
          navigate(`/?panel=families&tab=attendance&child_id=${result.id}`);
          toast.info("Select a classroom to check in");
          return;
        }
        close();
      } catch (err) {
        toast.error("Check-in failed", err instanceof ApiError ? err.errors.join(", ") : "An unexpected error occurred");
      }
      return;
    }
  }

  function getActionLabel(action: SubAction, result: SearchResult): string {
    if (action.id === "clock") {
      const s = clockStatus[result.id];
      if (!s || s.status === "loading") return "...";
      return s.status === "clocked_in" ? "Clock Out" : "Clock In";
    }
    if (action.id === "checkin") {
      const s = attendanceStatus[result.id];
      if (!s || s.status === "loading") return "...";
      return s.status === "checked_in" ? "Check Out" : "Check In";
    }
    return action.label;
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      if (expandedIndex !== null) {
        setExpandedIndex(null);
        setSubActionIndex(0);
      } else {
        close();
      }
      return;
    }

    // When a submenu is expanded
    if (expandedIndex !== null) {
      const resultIndex = expandedIndex - resultsOffset;
      const result = results[resultIndex];
      const actions = result ? getSubActions(result.record_type, result.id, userRole, user?.id) : undefined;
      if (!actions) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSubActionIndex((prev) => (prev < actions.length - 1 ? prev + 1 : 0));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSubActionIndex((prev) => (prev > 0 ? prev - 1 : actions.length - 1));
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setExpandedIndex(null);
        setSubActionIndex(0);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubAction(result, actions[subActionIndex]);
        return;
      }
      return;
    }

    // Normal navigation
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
      return;
    }
    if (e.key === "ArrowRight" && highlightedIndex >= resultsOffset) {
      const resultIndex = highlightedIndex - resultsOffset;
      const result = results[resultIndex];
      const actions = result ? getSubActions(result.record_type, result.id, userRole, user?.id) : undefined;
      if (result && actions && actions.length > 0) {
        e.preventDefault();
        setExpandedIndex(highlightedIndex);
        setSubActionIndex(0);
      }
      return;
    }
    if (e.key === "Enter") {
      // Always preventDefault when something is selectable so macOS/iOS
      // autocorrect (or any IME composition) can't consume Enter before
      // our handler runs. Falls back to the first item when the user
      // hasn't navigated with arrow keys yet.
      if (totalItems <= 0) return;
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      if (idx < matchedActionsOffset) {
        handleSelectSuggestion();
      } else if (idx < resultsOffset) {
        handleSelectAction(matchedActions[idx - matchedActionsOffset]);
      } else {
        const resultIndex = idx - resultsOffset;
        if (results[resultIndex]) navigateToResult(results[resultIndex]);
      }
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-overlay/40" onClick={close} />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-lg rounded-xl bg-surface shadow-softLg border border-border/40 overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border/30 px-4 py-3">
          <Search size={18} className="shrink-0 text-muted" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search children, parents, employees, directors, and more..."
            className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {loading && <Spinner className="shrink-0" />}
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto">
          {query.trim() === "" && <CapabilityHint />}

          {query.trim() !== "" && !loading && results.length === 0 && matchedActions.length === 0 && suggestionCount === 0 && gatedCount === 0 && (
            <CapabilityHint note={`No results found for “${query}”`} />
          )}

          {/* LLM intent suggestion (when available) */}
          {suggestion && (
            <SearchSuggestion
              suggestion={suggestion}
              isHighlighted={highlightedIndex === 0}
              onSelect={handleSelectSuggestion}
            />
          )}

          {/* Quick Actions — grouped by category */}
          {groupedActions.map((group) => {
            const meta = CATEGORY_META[group.category];
            const Icon = meta.icon;
            // compute running offset so highlightedIndex works across groups
            let offset = matchedActionsOffset;
            for (const g of groupedActions) {
              if (g.category === group.category) break;
              offset += g.actions.length;
            }

            return (
              <div key={group.category}>
                <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted">
                  {meta.label}
                </div>
                {group.actions.map((action, i) => {
                  const globalIdx = offset + i;
                  const isHighlighted = globalIdx === highlightedIndex;

                  return (
                    <button
                      key={action.id}
                      data-search-item
                      onClick={() => handleSelectAction(action)}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${isHighlighted
                        ? "bg-surface2"
                        : "hover:bg-surface2/60"
                        }`}
                    >
                      <span className="shrink-0 rounded bg-[#C4785C] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                        <Icon size={10} className="inline -mt-0.5 mr-0.5" />
                        {meta.label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {action.label}
                        </div>
                        <div className="truncate text-xs text-muted">
                          {action.subtitle}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* API Results */}
          {results.length > 0 && matchedActions.length > 0 && (
            <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted">
              Results
            </div>
          )}
          {results.map((result, index) => {
            const style = TYPE_STYLES[result.record_type] ?? TYPE_STYLES.child;
            const globalIndex = resultsOffset + index;
            const isHighlighted = globalIndex === highlightedIndex;
            const isExpanded = globalIndex === expandedIndex;
            const actions = getSubActions(result.record_type, result.id, userRole, user?.id);

            return (
              <div key={`${result.record_type}-${result.id}`}>
                <button
                  data-search-item
                  onClick={() => navigateToResult(result)}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    isHighlighted ? "bg-surface2" : "hover:bg-surface2/60"
                  }`}
                >
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white ${style.bg}`}
                  >
                    {style.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {result.display_name}
                    </div>
                    {result.subtitle && result.subtitle !== result.display_name && (
                      <div className="truncate text-xs text-muted">
                        {result.subtitle}
                      </div>
                    )}
                  </div>
                  {actions && actions.length > 0 && (
                    <span
                      role="button"
                      tabIndex={-1}
                      className="shrink-0 rounded p-1 hover:bg-surface2/60"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isExpanded) {
                          setExpandedIndex(null);
                          setSubActionIndex(0);
                        } else {
                          setHighlightedIndex(globalIndex);
                          setExpandedIndex(globalIndex);
                          setSubActionIndex(0);
                        }
                      }}
                    >
                      <ChevronRight
                        size={14}
                        className={`transition-transform ${
                          isExpanded ? "rotate-90 text-foreground" : "text-muted"
                        }`}
                      />
                    </span>
                  )}
                </button>

                {isExpanded && actions && (
                  <div className="mx-4 mb-2 flex flex-wrap gap-1.5 rounded-lg border border-border/20 bg-surface2/40 p-2">
                    {actions.map((action, actionIdx) => {
                      const Icon = action.icon;
                      const isActionHighlighted = actionIdx === subActionIndex;
                      return (
                        <button
                          key={action.id}
                          onClick={() => handleSubAction(result, action)}
                          className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                            isActionHighlighted
                              ? "bg-accent/10 text-accent ring-1 ring-accent/30"
                              : "bg-surface text-foreground hover:bg-surface2"
                          }`}
                        >
                          <Icon size={13} />
                          {getActionLabel(action, result)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tier teaser */}
        {gatedCount > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/20">
            <span className="text-xs text-muted">
              {gatedCount} more result{gatedCount !== 1 ? "s" : ""} available on Pro
            </span>
            <Link
              to="/billing/subscription"
              onClick={close}
              className="text-xs font-medium text-accent hover:underline"
            >
              Upgrade
            </Link>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-4 border-t border-border/30 px-4 py-2 text-[11px] text-muted">
          <span>
            <kbd className="rounded bg-surface2 px-1.5 py-0.5 font-mono text-[10px]">ESC</kbd>{" "}
            to close
          </span>
          <span>
            <kbd className="rounded bg-surface2 px-1.5 py-0.5 font-mono text-[10px]">&uarr;&darr;</kbd>{" "}
            to navigate
          </span>
          <span>
            <kbd className="rounded bg-surface2 px-1.5 py-0.5 font-mono text-[10px]">Enter</kbd>{" "}
            to select
          </span>
          <span>
            <kbd className="rounded bg-surface2 px-1.5 py-0.5 font-mono text-[10px]">&rarr;</kbd>{" "}
            actions
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}
